import { useCallback, useEffect, useState } from 'react'

// Components
import { IconButton, Typography, Box, Chip } from '@mui/material'
import { NavigateBefore, NavigateNext, Search } from '@mui/icons-material'
import ErrorDisplay from 'components/ErrorDisplay'
import CustomDatePicker from 'components/molecules/DatePicker/DatePicker'
import Button from 'components/molecules/Button/Button'
import { ProcedureListTable } from './ProcedureListTable'
import TextInput from 'components/molecules/TextInput/TextInput'
import ProcedureFilters from 'components/ProcedureFilters/ProcedureFilters'
import { TabPanel } from 'components/molecules/TablPanel/TabPanel'

// Router
import { useNavigate } from 'react-router-dom'

// Services
import { useDisablePolling } from 'lib/useDisablePolling'
import { useGetSurgeriesQuery } from 'lib/apollo/hooks'
import { TPatientStatusOption } from 'lib/services/restApi/organizations/organizations.types'

// Types
import { Surgery } from 'common/types'
import { EPatientStatus } from 'views/RoomView/RoomView.types'

// State
import { useFilters } from 'lib/hooks/useFilters'

// Utils
import { useIsMobile } from 'lib/utils/mediaQueries'
import { filterSurgeries } from 'lib/utils/filterSurgeries'
import { useFlags } from 'launchdarkly-react-client-sdk'
import dayjs from 'lib/dayjs'

// Styles
import './ProcedureListTable.scss'
import { useEnterpriseStore } from 'store'

const initialBefore = dayjs().endOf('day').toDate()
const initialAfter = dayjs().startOf('day').toDate()

function ProceduresNurse() {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const disablePolling = useDisablePolling()
  const [beforeDate, setBeforeDate] = useState(initialBefore)
  const [afterDate, setAfterDate] = useState(initialAfter)
  const flags = useFlags()
  const [search, setSearch] = useState<string>()
  const [surgeries, setSurgeries] = useState<Surgery[]>()
  const [filteredSurgeries, setFilteredSurgeries] = useState<Surgery[]>()
  const [filters, setFilters, clearFilters, isAnyFilterActive] = useFilters()
  const [selectedTab, setSelectedTab] = useState(0)

  // Dynamic navbar height calculation matching Layout.tsx logic
  const [isLg, setIsLg] = useState(window.innerWidth >= 600)
  const navbarHeight = isLg ? 64 : 56

  useEffect(() => {
    const handleResize = () => {
      setIsLg(window.innerWidth >= 600)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const { selectedDepartment, isDepartmentsLoaded } = useEnterpriseStore()

  const {
    loading: apolloLoading,
    error,
    data,
    refetch,
  } = useGetSurgeriesQuery(
    {
      before: beforeDate,
      after: afterDate,
      limit: 0,
      departmentId: selectedDepartment?.id,
    },
    {
      disablePolling,
      skip: !isDepartmentsLoaded,
    }
  )

  const loading = !isDepartmentsLoaded || apolloLoading

  const isErrored = !loading && error

  const handleBackDay = () => {
    setBeforeDate(dayjs(beforeDate).subtract(1, 'day').toDate())
    setAfterDate(dayjs(afterDate).subtract(1, 'day').toDate())
  }

  const handleForwardDay = () => {
    setBeforeDate(dayjs(beforeDate).add(1, 'day').toDate())
    setAfterDate(dayjs(afterDate).add(1, 'day').toDate())
  }

  const handleDateChange = (date: Date) => {
    setBeforeDate(dayjs(date).endOf('day').toDate())
    setAfterDate(dayjs(date).startOf('day').toDate())
  }

  const handleAddProcedure = () => {
    window.scrollTo(0, 0)
    navigate('/new-procedure')
  }
  const handleRoomView = () => {
    window.scrollTo(0, 0)
    navigate('/rooms')
  }

  const isCompletedSurgery = useCallback(
    (surgery: Surgery) => {
      const surgeryStatus = surgery.status?.name
      const patientStatus = surgery.patientStatusHistory?.[0]?.status

      if (surgeryStatus === 'CANCELLED') {
        return true
      }

      if (surgeryStatus === 'SUBMITTED') {
        if (flags?.enablePatientStatus) {
          return patientStatus === EPatientStatus.CHECKED_OUT
        }
        return true
      }

      return false
    },
    [flags?.enablePatientStatus]
  )

  const applySearchAndFilters = useCallback(() => {
    if (!surgeries) {
      setFilteredSurgeries(undefined)
      return
    }

    let results = surgeries

    if (selectedTab === 0) {
      results = surgeries.filter(
        (surgery: Surgery) => !isCompletedSurgery(surgery)
      )
    } else {
      results = surgeries.filter((surgery: Surgery) =>
        isCompletedSurgery(surgery)
      )
    }

    if (search) {
      const searchTerm = search.toLowerCase()

      results = results.filter((surgery: Surgery) => {
        const surgeonName = `${
          surgery.visit?.attendingProvider?.firstName || ''
        } ${surgery.visit?.attendingProvider?.lastName || ''}`.toLowerCase()
        const patientName =
          `${surgery.patient.firstName} ${surgery.patient.lastName}`.toLowerCase()
        const mrn = surgery.patient.idMR.toLowerCase()
        const roomNumber = `${surgery.visit?.location?.room}`.toLowerCase()

        if (searchTerm.includes('room')) {
          return roomNumber.includes(searchTerm.replace('room', '').trim())
        }

        return (
          surgeonName.includes(searchTerm) ||
          patientName.includes(searchTerm) ||
          mrn.includes(searchTerm)
        )
      })
    }

    results = filterSurgeries(results, filters)

    setFilteredSurgeries(results)
  }, [surgeries, search, filters, selectedTab, isCompletedSurgery])

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm)
  }, [])

  const handleApplyFilters = useCallback(
    (newFilters) => {
      setFilters(newFilters)
    },
    [setFilters]
  )

  // Add handlers for removing individual filters
  const handleRemoveLocationFilter = useCallback(
    (location) => {
      setFilters({
        ...filters,
        locations: filters.locations.filter((loc) => loc !== location),
      })
    },
    [filters, setFilters]
  )

  const handleRemoveRoomFilter = useCallback(
    (room) => {
      setFilters({
        ...filters,
        rooms: filters.rooms.filter((r) => r !== room),
      })
    },
    [filters, setFilters]
  )

  const handleRemoveSurgeonFilter = useCallback(() => {
    setFilters({
      ...filters,
      surgeonName: null,
    })
  }, [filters, setFilters])

  const handleRemoveSurgeryFilter = useCallback(() => {
    setFilters({
      ...filters,
      surgeryName: null,
    })
  }, [filters, setFilters])

  const handleRemovePatientStatusFilter = useCallback(() => {
    setFilters({
      ...filters,
      patientStatus: null,
    })
  }, [filters, setFilters])

  useEffect(() => {
    if (data?.getSurgeries?.results) {
      setSurgeries(data?.getSurgeries?.results)
    }
  }, [data?.getSurgeries?.results])

  useEffect(() => {
    applySearchAndFilters()
  }, [surgeries, search, filters, selectedTab, applySearchAndFilters])

  const getActiveFilterCounts = () => {
    return {
      sites: filters.locations.length,
      locations: filters.rooms.length,
      surgeon: filters.surgeonName ? 1 : 0,
      procedure: filters.surgeryName ? 1 : 0,
      patientStatus: filters.patientStatus ? 1 : 0,
    }
  }

  const activeFilterCounts = getActiveFilterCounts()
  const totalActiveFilters = Object.values(activeFilterCounts).reduce(
    (a, b) => a + b,
    0
  )

  return (
    <Box
      aria-label="procedure-list-container"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      gap={2}
      width="100%"
    >
      <Box className="procedures-header" style={{ top: `${navbarHeight}px` }}>
        {isMobile ? (
          <>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              mb={2}
            >
              {flags?.enablePatientStatus && (
                <Button onClick={handleRoomView} variant="contained">
                  Room View
                </Button>
              )}
              {flags?.allowAddingProceduresManually ? (
                <Button onClick={handleAddProcedure} variant="contained">
                  Add Procedure
                </Button>
              ) : null}
            </Box>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              mb={2}
            >
              <IconButton onClick={handleBackDay} size="small">
                <NavigateBefore fontSize="large" />
              </IconButton>
              <CustomDatePicker
                label="Procedure List Date"
                date={afterDate}
                renderDatePicker={true}
                onDateSelection={handleDateChange}
              />
              <IconButton onClick={handleForwardDay} size="small">
                <NavigateNext fontSize="large" />
              </IconButton>
            </Box>
          </>
        ) : (
          <Box
            aria-label="procedures-nurse-header"
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            mb={2}
          >
            {flags?.enablePatientStatus && (
              <Button onClick={handleRoomView} variant="contained">
                Room View
              </Button>
            )}
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton onClick={handleBackDay} size="small">
                <NavigateBefore fontSize="large" />
              </IconButton>
              <CustomDatePicker
                label="Procedure List Date"
                date={afterDate}
                renderDatePicker={true}
                onDateSelection={handleDateChange}
              />
              <IconButton onClick={handleForwardDay} size="small">
                <NavigateNext fontSize="large" />
              </IconButton>
            </Box>
            {flags?.allowAddingProceduresManually ? (
              <Button onClick={handleAddProcedure} variant="contained">
                Add Procedure
              </Button>
            ) : null}
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box flexGrow={1} className="search-container">
            <Search className="search-icon" />
            <TextInput
              variant="standard"
              size="small"
              className="search-input"
              value={search}
              placeholder={`Search ${
                isMobile
                  ? '(Patient, MRN, Surgeon, or Room)'
                  : '(by Patient Name, MRN, Surgeon, or Room Number)'
              }`}
              handleChangeFormData={(e: any) => handleSearch(e.target.value)}
            />
            <Box className="filter-container">
              <ProcedureFilters
                filters={filters}
                surgeries={surgeries}
                onClearFilters={clearFilters}
                onApplyFilters={handleApplyFilters}
                isAnyFilterActive={isAnyFilterActive}
              />
            </Box>
          </Box>
        </Box>

        {totalActiveFilters > 0 && (
          <Box
            display="flex"
            flexDirection="row"
            gap={1}
            flexWrap="wrap"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Active filters:
            </Typography>

            {filters.locations.map((location) => (
              <Chip
                key={`location-${location}`}
                label={location}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={() => handleRemoveLocationFilter(location)}
              />
            ))}

            {filters.rooms.map((room) => (
              <Chip
                key={`room-${room}`}
                label={`Room ${room}`}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={() => handleRemoveRoomFilter(room)}
              />
            ))}

            {activeFilterCounts.surgeon > 0 && (
              <Chip
                label={`Surgeon: ${filters.surgeonName}`}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={handleRemoveSurgeonFilter}
              />
            )}

            {activeFilterCounts.procedure > 0 && (
              <Chip
                label={`Surgery: ${filters.surgeryName}`}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={handleRemoveSurgeryFilter}
              />
            )}

            {activeFilterCounts.patientStatus > 0 && (
              <Chip
                label={`Status: ${(() => {
                  const options = JSON.parse(
                    localStorage.getItem('patientStatusOptions') || '[]'
                  )
                  const option = options.find(
                    (opt: TPatientStatusOption) =>
                      opt.status === filters.patientStatus
                  )
                  return option?.value || filters.patientStatus
                })()}`}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={handleRemovePatientStatusFilter}
              />
            )}

            <Button
              size="small"
              variant="text"
              color="secondary"
              onClick={clearFilters}
              className="clear-all-button"
            >
              Clear All
            </Button>
          </Box>
        )}

        <div className="tabs-container">
          <TabPanel
            tabsConfig={[
              {
                label: 'Scheduled',
                content: null,
              },
              {
                label: 'Completed',
                content: null,
              },
            ]}
            value={selectedTab}
            onChange={setSelectedTab}
            customTabPanelSx={{}}
          />
        </div>
      </Box>

      {/* If there is no surgery data and there is an error, show the 
      server connection message */}
      {isErrored && !surgeries && <ErrorDisplay pathForward={refetch} />}

      {/* If there is an error, but there are available surgeries, indicate to 
      the user without interrupting the normal app workflow, that one or more 
      of the surgeries could ot be displayed due to server errors, and to check 
      with the administrator.*/}
      {isErrored && surgeries && (
        <Typography variant="h4">
          Our server encountered an issue with 1 or more surgeries that could
          not be displayed. Please contact the administrator for further
          assistance.
        </Typography>
      )}

      <ProcedureListTable
        events={filteredSurgeries}
        loading={loading}
        beforeDate={beforeDate}
        afterDate={afterDate}
        refetch={refetch}
      />
    </Box>
  )
}

export default ProceduresNurse
